import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DynamicObjectInterface } from '../../../../sdk/type';
import {
  AdminPageHeader,
  ButtonStyles,
  DropDown,
  DropDownDataInterface,
  Input,
  InputTypes,
  FAIcon,
  IconStyle,
  MLPVersion,
  Theme,
  RadioButton,
} from '../../../../ui/components';
import { LoggerService } from '../../../../ui/utils';
import { useAdminToast } from '../../../../ui/components/AdminToast/AdminToast';
import { useAPICall } from '../../../../hooks';
import InventoryService from '../../../../sdk/services/inventoryService';
import RewardService from '../../../../sdk/services/rewardService';
import LocationService from '../../../../sdk/services/locationService';
import styles from './index.module.scss';

interface AdminProps {
  adminType: number;
}

export interface InventoryData {
  recId: number;
  locationCode: string;
  rewardCode: string;
  availableStock: number;
  redeemedStock: number;
  status: string;
}

export const AdminInventoryEditPage = ( { adminType }: AdminProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { adminToast } = useAdminToast();
  const id = searchParams.get('');
  const [inventoryData, setInventoryData] = useState<InventoryData | null>(null);
  const [adjustStock, setAdjustStock] = useState(0);
  const [initialStatus, setInitialStatus] = useState('');
  const [locationList, setLocationList] = useState([]);
  const [rewardList, setRewardList] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedReward, setSelectedReward] = useState('');
  const [invalid, setInvalid] = useState(true);

  const apiCall = useAPICall();
  const isNew = id === 'new';
  const params = new URLSearchParams(location.search);
  const campaignId = params.get('campaignId') ?? '';
  const isUnauthorized = adminType !== null && ![1].includes(adminType);

  // Use useEffect to update inventoryData when id changes
  useEffect(() => {
    (async () => {
      if (!isNew) {
        try {
          const response = await apiCall(InventoryService.getInventoryDetails, `${id}`);
          setInventoryData(response?.data?.data ?? {});
          setInitialStatus(response?.data?.data?.status);
          setSelectedLocation(response?.data?.data?.locationName);
          setSelectedReward(response?.data?.data?.rewardName);
        } catch (error) {
          LoggerService.log('Failed to retrieve inventory detail', error);
        }
      }else{
        setInventoryData(prev =>
          prev ? { ...prev, status: 'ACTIVE' } : { status: 'ACTIVE' } as InventoryData
        );
        fetchLocationList();
        fetchRewardList();
      }
    })();
  }, [id, isNew]);

  useEffect(() => {
    validateInputs();
  }, [inventoryData]);

  const handleInputChange = (name: string, value: DynamicObjectInterface) => {
    if (name === 'adjustStock') setAdjustStock(value);
    setInventoryData(prevInventoryData => ({
      ...prevInventoryData!,
      [name]: value,
    }));
  };

  const validateInputs = () => {
    const { rewardCode,locationCode, availableStock } = inventoryData ?? {};
    const isAnyFieldEmpty = isNew ? (!rewardCode || !locationCode || !availableStock ) : (!adjustStock && inventoryData?.status === initialStatus);
    setInvalid(isAnyFieldEmpty || isUnauthorized);
  };

  const onSave = async () => {
    const payload = {
      campaignId: campaignId,
      rewardCode: inventoryData?.rewardCode,
      locationCode: inventoryData?.locationCode,
      availableStock: inventoryData?.availableStock,
      redeemedStock: inventoryData?.redeemedStock || 0,
      adjustStock: adjustStock,
      status: inventoryData?.status,
    };

    const available = Number(inventoryData?.availableStock ?? 0);
    const change = Number(adjustStock);

    if(change < -available){
      adminToast({
        message: "The reduced stock can't be more than the available stock!",
        theme: Theme.ERROR,
      });
    }else {
      
      let result = isNew
        ? await apiCall(InventoryService.createInventory, payload)
        : await apiCall(InventoryService.updateInventory, `${id}`, payload);
      if (result.status === 200 && result?.data?.status) {
        navigate(`/admin/promotion/manage-inventory/list?campaignId=${campaignId}`);
        adminToast({
          message: result?.data?.message,
          theme: Theme.SUCCESS,
        });
      } else {
        navigate(`/admin/promotion/manage-inventory/list?campaignId=${campaignId}`);
        adminToast({
          message: 'Please try again later',
          theme: Theme.ERROR,
        });
      }
    }
  };

  const buttonConfig = [
    {
      title: t('admin.inventory.cancel'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/promotion/manage-inventory/list`);
      },
    },
    {
      title: t('admin.inventory.save'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: invalid,
      onClick: () => {
        onSave();
      },
    },
  ];

  const fetchLocationList = async () => {
    try {
      const result = await apiCall(LocationService.getLocationList);
      const locations = result?.data?.data?.map((location: DynamicObjectInterface) => ({
        id: location.locationCode,
        name: location.locationName,
      }));
      setLocationList(locations);
      // Handle the reward list data as needed
    } catch (error) {
      LoggerService.log('Failed to retrieve location list', error);
      // Handle error appropriately
    }
  };

    const fetchRewardList = async () => {
    try {
      const result = await apiCall(RewardService.getRewardList);
      const rewards = result?.data?.data
        ?.filter((reward: DynamicObjectInterface) => reward.enrollmentType === 'PROMOTION')
        .map((reward: DynamicObjectInterface) => ({
          id: reward.rewardCode,
          name: reward.rewardName,
        }));

      setRewardList(rewards);
      // Handle the reward list data as needed
    } catch (error) {
      LoggerService.log('Failed to retrieve reward list', error);
      // Handle error appropriately
    }
  };

  return (
    <div>
      <AdminPageHeader
        title={isNew ? t('admin.inventory.newTitle') : t('admin.inventory.editTitle')}
        button={buttonConfig}
      />
      <div className={styles['edit-reward-form-container']}>
        <div className={styles['input-container']}>
          <Input
            key={id}
            disabled
            type={InputTypes.TEXT}
            name={'campaignId'}
            label={`${t('admin.inventory.campaignId')}*`}
            value={campaignId}
            mlpVersion={MLPVersion.ONE}
          />
        </div>
        <div className={styles['input-container']}>
          <div className={styles['input']}>
            <div className={styles['input-label']}>{`${t('admin.inventory.status')}`}</div>
            <div className={styles['radio-button-container']}>
              <RadioButton
                label="Active"
                checked={inventoryData?.status === 'ACTIVE'}
                onClick={() => {
                  handleInputChange('status', 'ACTIVE');
                }}
              />
              <RadioButton
                label="Inactive"
                checked={inventoryData?.status === 'INACTIVE'}
                onClick={() => {
                  handleInputChange('status', 'INACTIVE');
                }}
              />
            </div>
          </div>
        </div>
        <div className={styles['input-container']}>
          <DropDown
            name={'locationName'}
            dropdownLabel={`${t('admin.inventory.locationName')}*`}
            disabled={!isNew}
            mlpVersion={MLPVersion.ONE}
            data={locationList}
            value={selectedLocation}
            configData={{
              label: 'name',
              onSelect: (selectedOptions: DropDownDataInterface) => {
                setSelectedLocation(selectedOptions.name.toString());
                handleInputChange('locationCode', selectedOptions?.id);
              },
            }}
          />
        </div>
        <div className={styles['input-container']}>
          <DropDown
            name={'locationName'}
            dropdownLabel={`${t('admin.inventory.rewardName')}*`}
            mlpVersion={MLPVersion.ONE}
            disabled={!isNew}
            data={isNew ? rewardList: []}
            value={selectedReward}
            configData={{
              label: 'name',
              onSelect: (selectedOptions: DropDownDataInterface) => {
                setSelectedReward(selectedOptions?.name.toString());
                handleInputChange('rewardCode', selectedOptions?.id);
              },
            }}
          />
        </div>
        <div className={styles['input-container']}>
          <Input
            key={id}
            disabled={!isNew}
            type={InputTypes.NUMBER}
            name={'availableStock'}
            label={`${t('admin.inventory.availableStock')}*`}
            value={inventoryData?.availableStock ?? 0}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('availableStock', e.target.value);
            }}
          />
        </div>
        <div className={styles['input-container']}>
          <Input
            key={id}
            disabled={true}
            type={InputTypes.NUMBER}
            name={'redeemedStock'}
            label={`${t('admin.inventory.redeemedStock')}*`}
            value={inventoryData?.redeemedStock ?? 0}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('redeemedStock', e.target.value);
            }}
          />
        </div>
         {!isNew && (
           <div className={styles['input-container']}>
            <Input
              key={id}
              disabled={true}
              type={InputTypes.NUMBER}
              name={'totalStock'}
              label={`${t('admin.inventory.totalStock')}`}
              value={Number(adjustStock) + Number(inventoryData?.availableStock ?? 0) + Number(inventoryData?.redeemedStock ?? 0)}
              mlpVersion={MLPVersion.ONE}
            />
          </div>
        )}

        {!isNew ? (
          <div className={styles['input-container']}>
            <div className={styles.inputWrapper}>
              <Input
                key={id}
                type={InputTypes.INTEGER}
                name="adjustStock"
                label={`${t('admin.inventory.adjustStock')}*`}
                value={adjustStock}
                mlpVersion={MLPVersion.ONE}
                onChange={(e) => handleInputChange('adjustStock', e.target.value)}
              />

              {/* Up & Down icons stacked together on the right side */}
              <div className={styles.iconGroup}>
                <FAIcon
                  className={styles.upIcon}
                  iconStyle={IconStyle.SOLID}
                  iconName="chevron-up"
                  theme={Theme.BLACK}
                  onClick={() =>
                    handleInputChange('adjustStock', Number(adjustStock || 0) + 1)
                  }
                />
                <FAIcon
                  className={styles.downIcon}
                  iconStyle={IconStyle.SOLID}
                  iconName="chevron-down"
                  theme={Theme.BLACK}
                  onClick={() =>
                    handleInputChange('adjustStock', Number(adjustStock || 0) - 1)
                  }
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
